import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { IDropdown, IVehicleType } from "../../models/BaseModel";
import { Fetch, ILazyParams } from "../../shared/Fetch";
import { Search } from "../../shared/Search";
import { IOpenForm } from "./VehicleType";
import { format } from "date-fns";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { AutoComplete } from "primereact/autocomplete";

interface Iprops {
    setModal: (modal: IOpenForm) => void,
    toast: any,
    reload: boolean,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Table = (props: Iprops) => {
    const [cities, setCities] = useState<IDropdown[]>();
    const [selectedCity, setSelectedCity] = useState<IDropdown>();
    const [tableData, setTableData] = useState({ data: [], count: 0, loading: false } as { data: IVehicleType[], count: number, loading: boolean });
    const [text, setText] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });

    useEffect(() => {
        loadTableData();
    }, [selectedCity, text, lazyParams, props.reload]);

    async function onCitySearch(text: string) {
        const response = await Fetch(`city/search`, "GET", null, `text=${text}`, null);
        if (response) {
            setCities(await response.json());
        }
    }

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const response = await Fetch("vehicletype", "GET", null, `lazyParams=${JSON.stringify(lazyParams)}${text ? '&text=' + text : ''}${selectedCity?.id ? '&cityId=' + selectedCity.id : ''}`, props.toast);
        if (response) {
            const data: { vehicleTypes: IVehicleType[], count: number } = await response.json();
            setTableData({ data: data.vehicleTypes, count: data.count, loading: false });
        } else {
            setTableData((prev) => ({ ...prev, loading: false, }));
        }
    }

    const openNew = () => {
        props.setModal({ cityId: selectedCity?.id, open: true, singleData: { isActive: true } });
    };

    const openEdit = (rd: IVehicleType) => {
        props.setModal({ cityId: rd.cityId, open: true, singleData: rd });
    };

    const Remove = async (rowData: IVehicleType) => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const response = await Fetch(`vehicletype/${rowData.id}`, "DELETE", null, null, props.toast);
        if (response) {
            props.toast.current.show({ severity: 'success', detail: "Vehicle Deleted", life: 3000 });
            loadTableData();
        }
    }

    const rejectFunc = () => { }

    const confirmDelete = (rowData: IVehicleType) => {
        confirmDialog({
            message: `Are you sure you want to delete "${rowData.name}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => Remove(rowData),
            reject: () => rejectFunc()
        });
    }

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-4">
            <div className="flex gap-4 w-full">
                {props.hasPermission("VehicleType", "Add") && (
                    <Button style={{ width: '80px', height: '30px' }} label="New" icon="pi pi-plus" className="p-button-sm p-button-rounded p-button-success" onClick={openNew} />
                )}
                <div className="w-full flex align-items-center justify-content-center">
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontFamily: 'Rancho', letterSpacing: 1 }}>Vehicle Type</h4>
                    <span style={{ margin: '0 10px' }} >City : </span>
                    <AutoComplete field="name" value={selectedCity} suggestions={cities} completeMethod={(e) => onCitySearch(e.query)} onChange={(e) => setSelectedCity(e.value)} />
                </div>
            </div>
            <div className="flex gap-4">
                <div className="p-input-icon-left">
                    <Search setSearch={setText} />
                </div>
                {props.hasPermission("VehicleType", "Export") && (
                    <Button style={{ width: '80px', height: '30px' }} label="Export" icon="pi pi-upload" className="p-button-sm p-button-rounded p-button-help" onClick={exportCSV} />
                )}
            </div>
        </div>
    );


    const actionBodyTemplate = (rowData: IVehicleType) => {
        return (
            <div className="actions">
                {props.hasPermission("VehicleType", "Edit") && <Button icon="pi pi-pencil" className='p-button-success p-button-text p-0' onClick={() => openEdit(rowData)} />}
                {props.hasPermission("VehicleType", "Remove") && <Button icon="pi pi-trash" className='p-button-danger p-button-text p-0' onClick={() => { confirmDelete(rowData) }}  ></Button>}
            </div>
        );
    };

    const statusBody = (rowData: IVehicleType) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${rowData.isActive ? 'pi-check-circle' : 'pi-times-circle'}`} style={{ color: rowData.isActive ? '#4caf50' : '#f44336' }}></i>
                <span>{rowData.isActive ? 'Active' : 'Disable'}</span>
            </div>
        );
    }


    return (
        <>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small" stripedRows scrollable scrollHeight="75vh" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={tableData.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={tableData.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={tableData.loading}
            >
                <Column field="id" header="ID" sortable />
                <Column field="name" header="Vehicle Type" sortable />
                <Column field="capacity" header="Capacity" sortable />
                {selectedCity?.id && <Column field="ratePerKm" header="Rate/Km" body={(r: IVehicleType) => r.ratePerKm ?? '--'} />}
                {selectedCity?.id && <Column field="minCharge" header="Min Charge" body={(r: IVehicleType) => r.minCharge ?? '--'} />}
                {selectedCity?.id && <Column field="driverCharge" header="Driver Charge" body={(r: IVehicleType) => r.driverCharge ?? '--'} />}
                {selectedCity?.id && <Column field="dayCharge" header="Day Charge" body={(r: IVehicleType) => r.dayCharge ?? '--'} />}
                {selectedCity?.id && <Column field="nightCharge" header="Night Charge" body={(r: IVehicleType) => r.nightCharge ?? '--'} />}
                <Column field="isActive" header="Status" sortable body={statusBody} />
                <Column field="desc" header="Desc" />
                <Column body={actionBodyTemplate} />
            </DataTable>
            <ConfirmDialog />
        </>
    );
}
